import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client/react'

import Document from 'js/components/Document'
import FetchMore from 'js/components/FetchMore'
import { Col, Container, Row } from 'js/components/Grid'
import ItemActions from 'js/components/Item/ItemActions/ItemActions'
import LoadingSpinner from 'js/components/LoadingSpinner/LoadingSpinner'
import Section from 'js/components/Section/Section'
import Text from 'js/components/Text/Text'
import HeaderLogo from 'js/magazine/components/HeaderLogo/HeaderLogo'
import styles from 'js/magazine/components/Layout/Layout.module.css'
import { MAGAZINE_ISSUES_LIST } from 'js/magazine/components/Layout/Layout.queries'
import MagazineIssueCard from 'js/magazine/components/Layout/MagazineIssueCard'
import getUrls from 'js/magazine/lib/getUrls'
import refetchQueries from 'js/magazine/lib/refetchQueries'
import ThemeProvider from 'js/theme/ThemeProvider'

type LayoutEntity = {
    guid: string
    title: string
    subtitle?: string
    icon?: { download?: string; url?: string }
    canEdit?: boolean
}

type LayoutProps = {
    entity: LayoutEntity
}

const Layout = ({ entity }: LayoutProps) => {
    const { t } = useTranslation()

    const { guid, title, subtitle, icon, canEdit } = entity

    const PAGE_SIZE = 9
    const { loading, data, fetchMore } = useQuery<any>(MAGAZINE_ISSUES_LIST, {
        variables: {
            parentGuid: guid,
            subtypes: ['magazine_issue'],
            offset: 0,
            limit: PAGE_SIZE,
        },
    })

    const urls = getUrls(guid)

    return (
        <ThemeProvider bodyColor="#FFFFFF">
            <Document title={title} />
            <Section
                className={styles.wrapper}
                backgroundColor="white" // darken Card shadow
            >
                <Container size="large">
                    <header className="layout-header">
                        <HeaderLogo
                            title={title}
                            subtitle={subtitle}
                            icon={icon}
                        />
                        <ItemActions
                            entity={entity as any}
                            onEdit={urls.edit}
                            onAfterDelete={urls.list}
                            hideArchive
                            showEditButton
                            refetchQueries={refetchQueries as any}
                            customOptions={[
                                [
                                    ...(canEdit
                                        ? [
                                              {
                                                  label: t(
                                                      'entity-magazine.create-issue',
                                                  ),
                                                  url: urls.addIssue,
                                              },
                                          ]
                                        : []),
                                ],
                            ]}
                            style={{ marginLeft: 'auto' }}
                        />
                    </header>
                    <article
                        style={{
                            position: 'relative',
                            marginTop: '32px',
                        }}
                    >
                        {loading ? (
                            <LoadingSpinner />
                        ) : data.entities.edges.length === 0 ? (
                            <Text textAlign="center">
                                {t('admin.content-not-found')}
                            </Text>
                        ) : (
                            <FetchMore
                                edges={data.entities.edges}
                                getMoreResults={(data) => data.entities.edges}
                                fetchMore={fetchMore}
                                fetchCount={9}
                                maxLimit={data.entities.total}
                                resultsMessage={t('global.result', {
                                    count: data.entities.total,
                                })}
                                fetchMoreButtonHeight={48}
                            >
                                <Row
                                    as="ul"
                                    $growContent
                                    $gutter={40}
                                    $spacing={40}
                                >
                                    {data.entities.edges.map((entity) => (
                                        <Col
                                            mobileLandscape={1 / 2}
                                            tabletUp={1 / 3}
                                            as="li"
                                            key={entity.guid}
                                        >
                                            <MagazineIssueCard
                                                {...({
                                                    entity,
                                                    showTheme: true,
                                                } as any)}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            </FetchMore>
                        )}
                    </article>
                </Container>
            </Section>
        </ThemeProvider>
    )
}

export default Layout
